<template>
	<TabView @tab-change="onTabChange($event.index)">
		<TabPanel :header="$t('Squeeze.DocumentClasses.FieldAttributes')">
			<div class="p-fluid p-formgrid p-grid p-input-filled">
				<div class="p-field p-col-6">
					<label for="name">{{$t('Squeeze.DocumentClasses.Name')}} *</label>
					<InputText id="name" v-model.trim="v$.name.$model" :class="{'p-invalid':v$.name.$invalid && showErrorMessage}" @change="onChangeInput" required="true" autofocus />
					<small v-if="v$.name.required.$invalid && showErrorMessage && !v$.name.technicalValidation.$invalid" class="p-error">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Name')}) }}
					</small><br/>
					<small v-if="v$.name.technicalValidation.$invalid && showErrorMessage" class="p-error">
						{{ $t('Forms.Val.OnlyAllowAlphaNumericUnderscore', { field: $t('Squeeze.DocumentClasses.Name')}) }}
					</small>
					<small v-else-if="!!value.id">
						{{ $t('Squeeze.Admin.WarnRename') }}
					</small>
				</div>
				<div class="p-field p-col-6">
					<label for="description">{{$t('Squeeze.DocumentClasses.Description')}} *</label>
					<DexInputText id="description" v-model.trim="v$.description.$model" :translatedDescription="value.translatedDescription" :translationKey="value.translationKey" :class="{'p-invalid':v$.description.$invalid && showErrorMessage}" @change="onChangeInput" required="true" />
					<small v-if="v$.description.$invalid && showErrorMessage" class="p-error">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Description')}) }}
					</small>
				</div>
				<div class="p-field p-col-6">
					<label for="dataType">{{$t('Squeeze.DocumentClasses.DataType')}} *</label>
					<Dropdown id="dataType" v-model="v$.dataType.$model" :options="dataTypes" optionValue="id" optionLabel="text" :class="{'p-invalid':v$.dataType.$invalid && showErrorMessage}" @change="onChangeDataType" />
					<small v-if="(!v$.dataType.$model && showErrorMessage)" class="p-error">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.DataType')}) }}
					</small>
				</div>
				<div class="p-field p-col-6">
					<label for="defaultValue">{{$t('Squeeze.DocumentClasses.DefaultValue')}}</label>
					<InputText id="defaultValue" v-model.trim="value.defaultValue" required="true" />
				</div>
				<div class="p-field p-col-6">
					<label for="locatorId">{{$t('Squeeze.DocumentClasses.Locator')}}</label>
					<Dropdown id="locatorId" @change="onChangeLocator(fieldEntry.locatorId)" v-model="value.locatorId" :options="locators" :filter="true" :autoFilterFocus="true" optionValue="id" :optionLabel="getTranslatedDescription" :showClear="true" :editable="false" @show="showDropdownOverlay" />
				</div>
				<div v-if="!tableField"  class="p-field p-col-6">
					<template v-if="subFields.length != 0">
						<label for="subFieldName">{{$t('Squeeze.DocumentClasses.Subfield')}}</label>
						<Dropdown id="subFieldName" v-model="value.subFieldName" :options="subFields" :filter="true" :autoFilterFocus="true" optionValue="subFieldName" optionLabel="name" :showClear="true" :editable="false" @show="showDropdownOverlay">
							<template #option="slotProps">
								<div class="p-dropdown-car-option">
									<span>{{slotProps.option.name}} ({{slotProps.option.subFieldName}})</span>
								</div>
							</template>
						</Dropdown>
					</template>
				</div>

				<div class="p-field p-col-6">
					<label for="fieldGroupId">{{$t('Squeeze.DocumentClasses.Group')}}</label>
					<Dropdown id="fieldGroupId" v-model="value.fieldGroupId" :options="fieldGroups" optionValue="id" :optionLabel="getTranslatedDescription" />
				</div>
				<div class="p-field p-col-6">
					<label for="externalName">{{$t('Squeeze.DocumentClasses.ExternalName')}}</label>
					<InputText id="externalName" v-model.trim="value.externalName" required="true" />
				</div>
				<div class="p-field p-col-3">
					<label for="mandatory">{{$t('Squeeze.DocumentClasses.Mandatory')}}</label><br/>
					<Checkbox id="mandatory" v-model="value.mandatory" :binary="true" @change="onChangeCheckBox('mandatory')" />
				</div>
				<div class="p-field p-col-3">
					<label for="readonly">{{$t('Squeeze.DocumentClasses.ReadOnly')}}</label><br/>
					<Checkbox id="readonly" v-model="value.readonly" :binary="true" @change="onChangeCheckBox('readonly')" />
				</div>
				<div class="p-field p-col-3">
					<label for="hidden">{{$t('Squeeze.DocumentClasses.Hidden')}}</label><br/>
					<Checkbox id="hidden" v-model="value.hidden" :binary="true" @change="onChangeCheckBox('hidden')" />
				</div>
				<div class="p-field p-col-3">
					<label for="forceValidation">{{$t('Squeeze.DocumentClasses.ForceValidation')}}</label><br/>
					<Checkbox id="forceValidation" v-model="value.forceValidation" :binary="true" @change="onChangeCheckBox('forceValidation')" />
				</div>
				<div class="p-field p-col-6">
					<label for="sameLineAsPreviousField">{{$t('Squeeze.DocumentClasses.ShowFieldNextToPreviousField')}}</label><br/>
					<Checkbox id="sameLineAsPreviousField" v-model="value.sameLineAsPreviousField" :binary="true" class="checkbox-previous-field" />
				</div>
				<div class="p-field p-col-3">
					<label for="trainable">{{$t('Squeeze.DocumentClasses.Trainable')}}</label><br/>
					<Checkbox id="trainable" v-model="value.trainable" :binary="true" @change="onChangeCheckBox('trainable')" class="checkbox-previous-field" :disabled="value.name === 'Creditor'" />
				</div>
				<div class="p-field p-col-3">
					<label for="useInReport">{{$t('Squeeze.DocumentClasses.UseInReport')}}</label><br/>
					<Checkbox id="useInReport" v-model="value.useInReport" :binary="true" @change="onChangeCheckBox('useInReport')" class="checkbox-previous-field" />
				</div>

				<template v-if="value.dataType === 'Text'">
					<div class="p-field p-col-6">
						<label for="maxLength">{{ $t('Squeeze.DocumentClasses.FieldLength') }}</label>
						<InputNumber id="maxLength" v-model="value.maxLength" />
					</div>
					<div class="p-field p-col-6">
						<label for="truncateType">{{ $t('Squeeze.DocumentClasses.CutContent') }}</label>
						<Dropdown
							id="truncateType"
							v-model="value.truncateType"
							:options="truncateTypes"
							optionValue="id"
							optionLabel="text"
							@show="showDropdownOverlay"
							:disabled="value.maxLength < 1"
						/>
					</div>
				</template>
			</div>
		</TabPanel>
		<TabPanel v-if="store.state.featureSet.configurableFieldAmountFormatting" :disabled="!value.dataType || value.dataType !== 'Amount'" :header="$t('Squeeze.General.Presentation')">
			<AmountSeparatorsForm
				:amountSeparatorData="{thousandSeparator: value.thousandSeparator, decimalSeparator: value.decimalSeparator, decimalPlaces: value.decimalPlaces}"
				@update="onChangeAmountSeparators"
			/>
		</TabPanel>
		<TabPanel :disabled="!value.dataType || value.dataType !== 'Text'" :header="$t('Squeeze.DocumentClasses.DbLookup')">
			<DbLookupForm :dbLookUpEntry="value.lookup" :showErrorMessage="showErrorMessage" @update="onUpdateFieldLookup" />
			<template v-if="value.id && value.lookup.active">
				<div class="p-mt-4">{{ $t('Squeeze.DocumentClasses.FieldFilterInfo') }}</div>
				<LookupFiltersView
					:document-class-id="fieldEntry.documentClassId"
					:field-id="fieldEntry.id"
					:table-id="fieldEntry.lookup.tableId"
				></LookupFiltersView>
			</template>
			<template v-else-if="value.lookup.active">
				{{ $t('Squeeze.DocumentClasses.PleaseSaveFieldFirst') }}
			</template>
		</TabPanel>
		<TabPanel v-if="showPromptsTab" :disabled="!value.id" :header="$t('Squeeze.Prompts.Prompts')">
			<PromptListView
				v-if="value.id"
				:contextDocumentClassId="value.documentClassId"
				:contextFieldName="value.name"
				:contextHasLlmClass="llmExtractionActive"
			/>
			<template v-else>
				{{ $t('Squeeze.DocumentClasses.PleaseSaveFieldFirst') }}
			</template>
		</TabPanel>
	</TabView>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, ref, watch} from "vue";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import DexInputText from "@/components/DexInputText.vue";
import InputNumber from 'primevue/inputnumber';
import Dropdown from "primevue/dropdown";
import {DocumentField, DocumentFieldGroup, DocumentLocator, LookupDefinition} from "@dex/squeeze-client-ts";
import {useI18n} from "vue-i18n";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import DbLookupForm from "@/apps/administration/components/documentclasses/DbLookupForm.vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import {ClientManager} from "@/singletons/ClientManager";
import {technicalValidation} from "@/util/ValidationHelper";
import LookupFiltersView from "@/apps/administration/views/squeeze/documentclasses/LookupFiltersView.vue";
import {showDropdownOverlay} from "@/util/StylesHelper";
import AmountSeparatorsForm, {AmountSeparator} from "@/apps/administration/components/documentclasses/AmountSeparatorsForm.vue";
import PromptListView from "@/apps/administration/views/squeeze/prompts/PromptListView.vue";
import useSqueezeStore from "@/apps/squeeze/store";
import {LLM_EXTRACTION_SETTING, useDocumentClassSettings} from "@/composables/useDocumentClassSettings";
import {FormState} from "@/util/Types";

type DocumentFieldFormState = FormState<DocumentField, "name" | "description" | "dataType">;

interface DataTypes {
	id: string | undefined;
	text: string;
}

export default defineComponent({
	name: "FieldForm",
	components: {
		AmountSeparatorsForm, Checkbox, InputText, DexInputText, InputNumber, Dropdown, DbLookupForm, TabView, TabPanel, LookupFiltersView, PromptListView,
	},
	props: {
		fieldEntry: {
			type: Object as PropType<DocumentField>,
			default: () => ({}),
			required: true,
		},
		locators: {
			type: Array as PropType<DocumentLocator[]>,
			default: () => [],
			required: true,
		},
		fieldGroups: {
			type: Array as PropType<DocumentFieldGroup[]>,
			default: () => [],
			required: true,
		},
		tableField: {
			type: Boolean,
			default: false,
		},
		showErrorMessage: {
			type: Boolean,
		},
	},
	emits: ['update', 'onTabChange'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const store = useSqueezeStore();

		/** Settings of the document class currently open (published by DocumentClassView) */
		const {isDocumentClassSettingActive} = useDocumentClassSettings();

		/** The Prompts tab needs the llmExtraction FeatureFlag and admin permissions */
		const showPromptsTab = computed<boolean>(() => {
			return Boolean(store.state.featureSet.llmExtraction) && Boolean(store.state.isAdminUser);
		});

		/** Is maxLLM enabled for the current document class? Drives the hint above the prompt table */
		const llmExtractionActive = computed<boolean>(() => isDocumentClassSettingActive(LLM_EXTRACTION_SETTING));

		/** Service for getting the locator-data */
		const locatorService = ClientManager.getInstance().squeeze.locator;

		/** Array for Locator-Subfield */
		const subFields = ref<DocumentField[] | null>([]);

		/** Current Object of all input-fields */
		const value = reactive(props.fieldEntry as DocumentFieldFormState);

		/** Determines the required rules for validation */
		const rules = {
			name: { required, technicalValidation },
			description: { required },
			dataType: { required },
		}

		/** List of Data-Types */
		const dataTypes = computed(() => {
			return [
				{
					id: 'Text',
					text: t('Squeeze.General.Text'),
				},
				{
					id: 'Date',
					text: t('Squeeze.General.Date'),
				},
				{
					id: 'Amount',
					text: t('Squeeze.General.Amount'),
				},
			] as DataTypes[]
		});

		/** List of Truncate-Types */
		const truncateTypes = computed(() => {
			return [
				{
					id: DocumentField.TruncateTypeEnum.None,
					text: t('Squeeze.DocumentClasses.DoNotCutOff'),
				},
				{
					id: DocumentField.TruncateTypeEnum.Start,
					text: t('Squeeze.DocumentClasses.CutOffStartOfField'),
				},
				{
					id: DocumentField.TruncateTypeEnum.End,
					text: t('Squeeze.DocumentClasses.CutOffEndOfField'),
				},
			];
		});

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value, { $scope: false });

		const onTabChange = (activeTabIndex: number) => {
			emit("onTabChange", activeTabIndex);
		}

		/**
		 * Returns the Translation for a subfield
		 * @param subFieldId Subfield-ID of the Subfield to translate
		 * @returns Translated Sub-Field or Subfield-Id if it can't be found
		 */
		const getSubFieldTranslation = (subFieldId: string) => {
			// FIXME: Currently the Translation is taken from i18n. Generally the correct name should be returned from the API

			if (t('Squeeze.Locators.SubFields.' + subFieldId)) {
				return t('Squeeze.Locators.SubFields.' + subFieldId)
			}

			return String(subFieldId)
		}

		/**
		 * Gets the Description of a locator
		 * @param locatorId Id of the Locator to get the Description from
		 * @return Type of the Locator
		 */
		const getTypeFromLocator = (locatorId: number) => {
			if (!props.locators) {
				return 0
			}
			const locator = props.locators.find(i => i.id === locatorId);
			if (!locator) {
				return 0
			}

			return locator.locatorType;
		}

		/**
		 * Sets the list of the Sub-Fields in correlation to the current locator
		 * @param  {number} locatorId Locator-ID of the current locator
		 */
		const setSubFieldList = (locatorId: number) => {
			const locatorType = getTypeFromLocator(locatorId);
			locatorService.getSupportedLocatorSubFields(String(locatorType)).then((data) => {
				data.forEach(subField => {
					if (subField.subFieldName) {
						subField.name = getSubFieldTranslation(subField.subFieldName)
					}
				})

				subFields.value = data
			})
		}

		onMounted(async () => {
			if (props.fieldEntry && props.fieldEntry.locatorId) {
				setSubFieldList(props.fieldEntry.locatorId)
			}

			Object.assign(value, props.fieldEntry);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.fieldEntry, () => {
			Object.assign(value, props.fieldEntry);
		})

		/**
		 * Triggered when a Locator is changed
		 * @param {number} locatorId Locator-ID of the current locator
		 */
		const onChangeLocator = (locatorId: number) => {
			setSubFieldList(locatorId)
		}

		/** Triggered on change of any field */
		const update = async (isInvalidLookup?: boolean) => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			if (isInvalidLookup) {
				emit("update", value, true, 1);
			} else{
				emit("update", value, v$.value.$invalid, 0);
			}
		}

		/** Triggered on change of any field lookup */
		const onUpdateFieldLookup = async (lookupField: LookupDefinition, isInvalid: boolean) => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			Object.assign(value.lookup, lookupField)
			await update(isInvalid);
		}

		/**
		 * Set the lookup to inactive if the Type is not text
		 */
		const onChangeDataType = () => {
			if (value.dataType !== "Text") {
				value.lookup!.active = false;
			}
			update();
		}

		/** Triggered on change of input field */
		const onChangeInput = () => {
			update();
		}

		/**
		 * Triggered on change of an amount separator
		 * @param data
		 */
		const onChangeAmountSeparators = (data: AmountSeparator) => {
			value.thousandSeparator = data.thousandSeparator;
			value.decimalSeparator = data.decimalSeparator;
			value.decimalPlaces = data.decimalPlaces;
			update();
		}

		/**
		 * Is triggered when a checkbox from mandatory, forceValidation or hidden is clicked
		 * @param fieldName Name of the field
		 */
		const onChangeCheckBox = (fieldName: string) => {
			switch(fieldName) {
			case 'mandatory':
			case 'forceValidation': {
				// If a field is mandatory or "force Validation", it shouldn't be hidden
				if (value.mandatory === true || value.forceValidation === true) {
					value.hidden = false;
				}
				break;
			}
			case 'hidden': {
				// If a field is hidden, forcing the Validation can cause a falsy behavior
				if (value.hidden) {
					value.mandatory = false;
					value.forceValidation = false;
				}
				break;
			}
			}
		}

		return {
			getTranslatedDescription,
			t,
			store,
			showPromptsTab,
			llmExtractionActive,
			locatorService,
			v$,
			value,
			dataTypes,
			truncateTypes,
			subFields,
			onTabChange,
			onChangeLocator,
			setSubFieldList,
			getTypeFromLocator,
			getSubFieldTranslation,
			onChangeDataType,
			onChangeInput,
			onChangeAmountSeparators,
			onChangeCheckBox,
			update,
			onUpdateFieldLookup,
			showDropdownOverlay,
		}
	},
});
</script>

<style scoped>
/** Otherwise there will be scrollbar at the bottom */
.p-grid {
	margin-left: 0px;
	margin-right: 0px;
}

.checkbox-previous-field {
	vertical-align: text-top;
	margin-right: 0.5rem;
}

</style>
